import { create } from "zustand";

const useUserManagementStore = create((set) => ({
  users: [],
  availableRoles: [],
  isLoading: false,
  error: null,

  getAuthHeaders: () => {
    const token = localStorage.getItem("token");
    const tenantId = localStorage.getItem("tenantId");
    const role = localStorage.getItem("role");
    console.log("getAuthHeaders - Token:", token, "TenantId:", tenantId, "Role:", role);

    if (!token) {
      console.error("No token found in localStorage");
      return {
        headers: {
          "Content-Type": "application/json",
          Authorization: "",
        },
        params: "",
      };
    }

    const params = new URLSearchParams();
    if (tenantId && tenantId !== "null" && role !== "SuperAdmin") {
      params.append("tenantId", tenantId);
    }
    if (role === "SuperAdmin") {
      params.append("allTenants", "true");
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    console.log(
      "getAuthHeaders - Constructed headers:",
      headers,
      "Params:",
      params.toString()
    );

    return {
      headers,
      params: params.toString(),
    };
  },

  fetchAvailableRoles: async (tenantId = null) => {
    set({ isLoading: true, error: null });
    try {
      let params;
      const { headers } = useUserManagementStore.getState().getAuthHeaders();
      const role = localStorage.getItem("role");

      // If a tenantId is provided (e.g., by SuperAdmin selecting an organization), override the default params
      if (tenantId) {
        params = new URLSearchParams();
        params.append("tenantId", tenantId);
      } else {
        // Otherwise, use the default getAuthHeaders logic
        const authHeaders = useUserManagementStore.getState().getAuthHeaders();
        params = authHeaders.params;
      }

      const url = `${process.env.REACT_APP_API_URL}api/role/getavailableroles${params ? `?${params}` : ""}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      console.log("fetchAvailableRoles - URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Fetch available roles error response:", errorText);
        throw new Error("Failed to fetch available roles");
      }

      const data = await response.json();
      // Map roles to extract role names as strings
      const roleNames = (data.roles || []).map(role => role.role);
      set({ availableRoles: roleNames, isLoading: false });
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Error fetching available roles:", error);
    }
  },

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useUserManagementStore.getState().getAuthHeaders();
      console.log("fetchUsers - Headers:", headers, "Params:", params);

      if (!headers.Authorization) {
        throw new Error("No valid Authorization header - Please login again");
      }

      const url = `${process.env.REACT_APP_API_URL}api/user-management/getallusers${params ? `?${params}` : ""}`;
      console.log("Fetch URL:", url);

      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        method: "GET",
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Fetch error response:", errorText);
        if (response.status === 401) {
          throw new Error("Unauthorized - Please login again");
        }
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin access required");
        }
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      set({ users: data.users || [], isLoading: false });
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Error fetching users:", error);
      if (error.message.includes("Unauthorized")) {
        console.error("Redirecting to login is suggested.");
      }
    }
  },

  addUser: async (userData) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useUserManagementStore.getState().getAuthHeaders();
      const role = localStorage.getItem("role");

      const requestBody = { ...userData };

      console.log("addUser - URL:", `${process.env.REACT_APP_API_URL}api/user-management/createuser${params ? `?${params}` : ""}`, "Body:", requestBody, "User Role:", role);

      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/user-management/createuser${params ? `?${params}` : ""}`, {
        method: "POST",
        headers: fetchHeaders,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Add user error response:", errorText);
        const errorData = JSON.parse(errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin access required");
        }
        throw new Error(errorData.message || "Failed to add user");
      }

      const data = await response.json();
      set((state) => ({
        users: [...state.users, data.user],
        isLoading: false,
        error: null,
      }));
      return data.user;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false,
      });
      console.error("Error adding user:", error);
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useUserManagementStore.getState().getAuthHeaders();
      const role = localStorage.getItem("role");

      const requestBody = { ...userData };

      console.log("updateUser - URL:", `${process.env.REACT_APP_API_URL}api/user-management/updateuser/${userId}${params ? `?${params}` : ""}`, "Body:", requestBody, "User Role:", role);

      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/user-management/updateuser/${userId}${params ? `?${params}` : ""}`, {
        method: "PUT",
        headers: fetchHeaders,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Update user error response:", errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin access required");
        }
        throw new Error(response.statusText || "Failed to update user");
      }

      const data = await response.json();
      set((state) => ({
        users: state.users.map((user) => (user._id === userId ? data.updatedUser : user)),
        isLoading: false,
        error: null,
      }));
      return data.updatedUser;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false,
      });
      console.error("Error updating user:", error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useUserManagementStore.getState().getAuthHeaders();
      const url = `${process.env.REACT_APP_API_URL}api/user-management/deleteuser/${userId}${params ? `?${params}` : ""}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        method: "DELETE",
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Delete user error response:", errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin access required");
        }
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Cannot delete this user");
        }
        throw new Error(response.statusText || "Failed to delete user");
      }

      set((state) => ({
        users: state.users.filter((user) => user._id !== userId),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      set({
        error: error.message,
        isLoading: false,
      });
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  searchUser: async (searchTerm) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useUserManagementStore.getState().getAuthHeaders();
      const queryString = new URLSearchParams({
        ...searchTerm,
        ...(params ? Object.fromEntries(new URLSearchParams(params)) : {}),
      }).toString();
      const url = `${process.env.REACT_APP_API_URL}api/user-management/searchuser?${queryString}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Search user error response:", errorText);
        throw new Error("Failed to search users");
      }

      const data = await response.json();
      set({ users: data.users || [], isLoading: false });
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Error searching user:", error);
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));

export default useUserManagementStore;